// Apolo IDE — E2b · useSteering hook.
//
// Loads steering sources from the local filesystem via Electron IPC
// (window.apolo.steering.load). Web (no Electron) → sources = [] (no throw).
// Caches per root: loads once per root, turns reuse the cached value.
// sourcesRef is kept in sync so stable send callbacks can read the latest
// value without being in deps arrays.

import { useEffect, useRef, useState } from 'react';

/**
 * Load project steering sources from .apollo/steering/*.md.
 *
 * @param {string | undefined | null} root - The session root directory (must start with '/').
 * @returns {{ sources: SteeringSource[], count: number, sourcesRef: React.MutableRefObject<SteeringSource[]> }}
 */
export function useSteering(root) {
  const [sources, setSources] = useState([]);
  const sourcesRef = useRef([]);
  // Track whether the component is still mounted (guard setState-after-unmount)
  const mountedRef = useRef(true);
  // Track which root has been loaded (cache per session)
  const loadedRootRef = useRef(null);

  // Keep sourcesRef in sync with sources state
  useEffect(() => {
    sourcesRef.current = sources;
  }, [sources]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Guard: root must be a non-empty string starting with '/'
    if (!root || typeof root !== 'string' || !root.startsWith('/')) {
      setSources([]);
      sourcesRef.current = [];
      loadedRootRef.current = null;
      return;
    }

    // Cache: already loaded for this root
    if (loadedRootRef.current === root) return;

    // Guard: IPC must be available (Electron only)
    const loadFn = window.apolo?.steering?.load;
    if (typeof loadFn !== 'function') {
      setSources([]);
      sourcesRef.current = [];
      return;
    }

    // Mark as loaded before the async call to prevent double-load on StrictMode
    loadedRootRef.current = root;

    let cancelled = false;
    (async () => {
      try {
        const result = await loadFn({ root });
        if (cancelled || !mountedRef.current) return;
        const safe = Array.isArray(result) ? result : [];
        setSources(safe);
        sourcesRef.current = safe;
      } catch {
        // IPC failure → safe fallback, never throw
        if (cancelled || !mountedRef.current) return;
        setSources([]);
        sourcesRef.current = [];
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [root]);

  return { sources, count: sources.length, sourcesRef };
}

export default useSteering;
